import {
  DataGridPro,
  GridActionsCellItem,
  GridColumns,
  GridCsvExportOptions,
  GridRowParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValueGetterParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import React, { useState } from 'react';
import { LoadingProgress } from 'src/common/store/types';
import { PagedResult } from 'src/store';
import { ScheduledEvent } from '../../store/types';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import WO2Button from 'src/common/components/button/Button';
import { Add, Delete, Download, Edit } from '@mui/icons-material';
import { DateTimeFormat, getLocalDateTime } from 'src/common';
import { useHistory } from 'react-router-dom';
import { DeleteScheduledEventModal } from './DeleteScheduledEventModal';

interface Props {
  page: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  setPage: (page: number) => void;
  fetchScheduledEventListProgress: LoadingProgress;
  deleteScheduledEventProgress: LoadingProgress;
  deleteScheduledEvent: (id: string) => void;
  paginatedEventListResults: PagedResult<ScheduledEvent>;
}

export const ReportingSchedulerTable = (props: Props): JSX.Element => {
  const history = useHistory();
  const [deletingRow, setDeletingRow] = useState<ScheduledEvent | null>(null);

  const columns: GridColumns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 1 },
    {
      field: 'occurrences',
      headerName: 'Occurrences',
      flex: 1,
      valueGetter: (params: GridValueGetterParams<ScheduledEvent>) => {
        return params.row.occurrences > 0 ? `${params.row.occurrences}` : '';
      },
    },
    { field: 'recurrenceType', headerName: 'Recurrence Type', flex: 1 },
    {
      field: 'startOn',
      headerName: 'Started on*',
      flex: 1,
      valueGetter: (params) => getLocalDateTime(params.value, DateTimeFormat.DayDate),
    },
    {
      field: 'endOn',
      headerName: 'Ends on*',
      flex: 1,
      valueGetter: (params) => getLocalDateTime(params.value, DateTimeFormat.DayDate),
    },
    {
      field: 'nextRunOn',
      headerName: 'Next run on*',
      flex: 1,
      valueGetter: (params) => getLocalDateTime(params.value, DateTimeFormat.DayDate),
    },
    {
      field: 'calculatedRemainingInstances',
      headerName: 'Remaining Occurences',
      flex: 1,
      valueGetter: (params) => {
        const { recurrenceType, calculatedFinalRunOn, calculatedRemainingInstances } = params.row;
        if (recurrenceType === 'Once') {
          return 'One-time event (does not repeat).';
        }
        if (calculatedRemainingInstances > 0) {
          return `${calculatedRemainingInstances} remaining occurrence(s). Final run on ${getLocalDateTime(
            calculatedFinalRunOn,
            DateTimeFormat.DayDate
          )}.`;
        }
        return 'No remaining occurrences.';
      },
    },
    {
      field: 'createdOn',
      headerName: 'Created on',
      flex: 1,
      valueGetter: (params) => getLocalDateTime(params.value, DateTimeFormat.DayDate),
    },
    {
      field: 'Actions',
      type: 'actions',
      sortable: false,
      width: 120,
      getActions: (params: GridRowParams<ScheduledEvent>) => [
        <GridActionsCellItem
          key="edit"
          onClick={() => history.replace(`/scheduler/edit/${params.row.id}`)}
          icon={
            <Tooltip title="Edit">
              <Edit />
            </Tooltip>
          }
          label="Edit"
          color="primary"
        />,
        <GridActionsCellItem
          key="delete"
          onClick={() => setDeletingRow(params.row)}
          icon={
            <Tooltip title="Delete">
              <Delete />
            </Tooltip>
          }
          label="Delete"
          color="primary"
        />,
      ],
    },
  ];

  const tableToolbar = () => {
    const apiRef = useGridApiContext();
    const handleExport = (options?: GridCsvExportOptions) => apiRef.current.exportDataAsCsv(options);

    return (
      <GridToolbarContainer sx={{ padding: 2, alignItems: 'center' }}>
        <Typography>* Dates are in Sydney time zone</Typography>
        <Box flex={1} />
        <Box>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <GridToolbarColumnsButton />
            </Grid>
            <Grid item>
              <GridToolbarFilterButton />
            </Grid>
            <Grid item>
              <WO2Button onClick={() => handleExport({ fileName: 'ScheduledEvents' })} startIcon={<Download />}>
                Export
              </WO2Button>
            </Grid>
            <Grid item>
              <WO2Button onClick={() => history.replace('/scheduler/create')} startIcon={<Add />}>
                Create
              </WO2Button>
            </Grid>
          </Grid>
        </Box>
      </GridToolbarContainer>
    );
  };

  return (
    <>
      <DataGridPro
        initialState={{
          pagination: {
            page: 0,
            pageSize: 25,
          },
        }}
        componentsProps={{
          toolbar: props,
        }}
        components={{
          Toolbar: tableToolbar,
        }}
        checkboxSelection
        autoHeight
        pagination
        page={props.page}
        getRowId={(row) => row.id}
        rowCount={props.paginatedEventListResults.totalNumberOfRecords}
        loading={props.fetchScheduledEventListProgress.isLoading}
        onPageChange={(page: number) => {
          props.setPage(page);
        }}
        disableSelectionOnClick
        onPageSizeChange={(pageSize: number) => {
          props.setPageSize(pageSize);
        }}
        paginationMode="server"
        columns={columns}
        rows={props.paginatedEventListResults.results}
      />

      {deletingRow && (
        <DeleteScheduledEventModal
          isOpen={deletingRow !== null}
          handleClose={() => setDeletingRow(null)}
          deleteScheduledEvent={props.deleteScheduledEvent}
          deleteScheduledEventProgress={props.deleteScheduledEventProgress}
          row={deletingRow}
        />
      )}
    </>
  );
};
